/**
 * KPI Cards Component
 *
 * Displays portfolio metrics dashboard cards with sparklines
 * Memoized for performance optimization
 */

import { memo } from 'react';
import { Sparkline } from '../visualizations/Sparkline';
import type { TradingSignal } from '../../hooks/useTradingSignals';

interface PortfolioMetrics {
  totalValue: number;
  totalPL: number;
  plPercent: number;
}

interface KPICardsProps {
  portfolioMetrics: PortfolioMetrics;
  portfolioSparklineData: number[];
  signalsSparkline: number[];
  sparklineLoading: boolean;
  tradingSignals: TradingSignal[];
}

/**
 * Portfolio Value Card
 */
const PortfolioValueCard = memo(function PortfolioValueCard({
  totalValue,
  sparklineData,
  loading
}: {
  totalValue: number;
  sparklineData: number[];
  loading: boolean;
}) {
  return (
    <div className="glass-card" style={{ padding: '18px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600 }}>PORTFOLIO VALUE</div>
        <div style={{
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 700,
          background: 'rgba(139, 92, 246, 0.15)',
          color: '#8b5cf6',
          display: 'flex',
          alignItems: 'center',
          gap: '2px'
        }}>
          💰 Live
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>
        ${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </div>
      <div style={{ marginTop: '12px' }}>
        <Sparkline
          data={sparklineData.length > 0 ? sparklineData : [0, 0, 0, 0, 0, 0, 0]}
          width={160}
          height={32}
          color="#8b5cf6"
          fillColor="rgba(139, 92, 246, 0.1)"
        />
      </div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px' }}>
        {loading ? 'Loading...' : 'Current value'}
      </div>
    </div>
  );
});

/**
 * Total P/L Card
 */
const TotalPLCard = memo(function TotalPLCard({
  totalPL,
  plPercent
}: {
  totalPL: number;
  plPercent: number;
}) {
  return (
    <div className="glass-card" style={{ padding: '18px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600 }}>TOTAL P/L</div>
        <div style={{
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 700,
          background: totalPL >= 0 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
          color: totalPL >= 0 ? '#10b981' : '#ef4444',
          display: 'flex',
          alignItems: 'center',
          gap: '2px'
        }}>
          {totalPL >= 0 ? '📈' : '📉'} Real
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: totalPL >= 0 ? '#10b981' : '#ef4444', marginBottom: '4px' }}>
        {totalPL >= 0 ? '+' : ''}${totalPL.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </div>
      <div style={{ fontSize: '13px', color: totalPL >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
        {totalPL >= 0 ? '+' : ''}{plPercent.toFixed(1)}%
      </div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px' }}>
        {totalPL >= 0 ? 'Profitable' : 'Drawdown'}
      </div>
    </div>
  );
});

/**
 * Active Signals Card
 */
const ActiveSignalsCard = memo(function ActiveSignalsCard({
  signalsCount,
  highConfidenceCount,
  sparklineData
}: {
  signalsCount: number;
  highConfidenceCount: number;
  sparklineData: number[];
}) {
  return (
    <div className="glass-card" style={{ padding: '18px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600 }}>ACTIVE SIGNALS</div>
        <div style={{
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 700,
          background: 'rgba(139, 92, 246, 0.15)',
          color: '#8b5cf6',
          display: 'flex',
          alignItems: 'center',
          gap: '2px'
        }}>
          Live
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>
        {signalsCount}
      </div>
      <div style={{ marginTop: '12px' }}>
        <Sparkline
          data={sparklineData}
          width={160}
          height={32}
          color="#8b5cf6"
          fillColor="rgba(139, 92, 246, 0.1)"
        />
      </div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px' }}>
        {highConfidenceCount} high confidence
      </div>
    </div>
  );
});

/**
 * Average Confidence Card
 */
const AvgConfidenceCard = memo(function AvgConfidenceCard({
  signals
}: {
  signals: TradingSignal[];
}) {
  const avgConfidence = signals.length > 0
    ? Math.round(signals.reduce((sum, s) => sum + s.confidence, 0) / signals.length)
    : 0;
  const veryHighCount = signals.filter(s => s.confidence >= 80).length;

  return (
    <div className="glass-card" style={{ padding: '18px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600 }}>AVG CONFIDENCE</div>
        <div style={{
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 700,
          background: 'rgba(139, 92, 246, 0.15)',
          color: '#8b5cf6',
          display: 'flex',
          alignItems: 'center',
          gap: '2px'
        }}>
          📊
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: '#8b5cf6', marginBottom: '4px' }}>
        {avgConfidence}%
      </div>
      <div style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 600, marginBottom: '8px' }}>
        Across {signals.length} signals
      </div>
      <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
        {veryHighCount > 0
          ? `✨ ${veryHighCount} very high`
          : '📈 Quality signals'}
      </div>
    </div>
  );
});

/**
 * Signal Distribution Card
 */
const SignalDistributionCard = memo(function SignalDistributionCard({
  signals
}: {
  signals: TradingSignal[];
}) {
  const buyCount = signals.filter(s => s.action === 'buy').length;
  const holdCount = signals.filter(s => s.action === 'hold').length;
  const sellCount = signals.filter(s => s.action === 'sell').length;

  const marketSentiment = buyCount > sellCount
    ? '🟢 Bullish overall'
    : sellCount > buyCount
    ? '🔴 Bearish overall'
    : '🟡 Neutral overall';

  return (
    <div className="glass-card" style={{ padding: '18px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600 }}>SIGNAL MIX</div>
        <div style={{
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 700,
          background: 'rgba(59, 130, 246, 0.15)',
          color: '#3b82f6',
          display: 'flex',
          alignItems: 'center',
          gap: '2px'
        }}>
          📈
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#10b981' }}>
            {buyCount}
          </div>
          <div style={{ fontSize: '10px', color: '#10b981', fontWeight: 600 }}>BUY</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#f59e0b' }}>
            {holdCount}
          </div>
          <div style={{ fontSize: '10px', color: '#f59e0b', fontWeight: 600 }}>HOLD</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#ef4444' }}>
            {sellCount}
          </div>
          <div style={{ fontSize: '10px', color: '#ef4444', fontWeight: 600 }}>SELL</div>
        </div>
      </div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
        {marketSentiment}
      </div>
    </div>
  );
});

/**
 * KPI Cards Grid Component
 */
export const KPICards = memo(function KPICards({
  portfolioMetrics,
  portfolioSparklineData,
  signalsSparkline,
  sparklineLoading,
  tradingSignals
}: KPICardsProps) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
      <PortfolioValueCard
        totalValue={portfolioMetrics.totalValue}
        sparklineData={portfolioSparklineData}
        loading={sparklineLoading}
      />
      <TotalPLCard
        totalPL={portfolioMetrics.totalPL}
        plPercent={portfolioMetrics.plPercent}
      />
      <ActiveSignalsCard
        signalsCount={tradingSignals.length}
        highConfidenceCount={tradingSignals.filter(s => s.confidence > 75).length}
        sparklineData={signalsSparkline}
      />
      <AvgConfidenceCard signals={tradingSignals} />
      <SignalDistributionCard signals={tradingSignals} />
    </div>
  );
});

KPICards.displayName = 'KPICards';
